import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import authService from "@/services/authService";
import { extractErrorMessage } from "@/utils/errors";

interface ForgotPasswordFormValues {
  email: string;
}

export function ForgotPasswordPage() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>();

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      await authService.forgotPassword(values.email);
      setSubmitted(true);
    } catch (error) {
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Stack spacing={2}>
        <Alert severity="success">
          If an account with that email exists, a password reset link has been sent.
        </Alert>
        <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
          Back to sign in
        </Link>
      </Stack>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}

        <TextField
          label="Email"
          type="email"
          fullWidth
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          {...register("email", { required: "Email is required" })}
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? "Sending..." : "Send reset link"}
        </Button>

        <Link component={RouterLink} to="/login" variant="body2" textAlign="center">
          Back to sign in
        </Link>
      </Stack>
    </Box>
  );
}

export default ForgotPasswordPage;
